import { Button, Label, Modal, Select, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { WorkShift } from "@/sharedTypes";
import { weekDays, timeZones } from "@/constants/workShift";
import { useTranslation } from "react-i18next";

interface WorkShiftFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  mode?: "create" | "edit";
  initialData?: WorkShift | null;
  errorMessage?: string;
}

export default function WorkShiftFormDialog({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
  errorMessage,
}: WorkShiftFormDialogProps) {
  const { t } = useTranslation(["workShift", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<WorkShift>();

  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        // Reset form with initial data
        reset({
            ...(initialData as any), // Cast to any to avoid strict type issues with form fields
        });
        
        // Handle shift_days
        const currentShiftDays = initialData.shift_days || [];
        
        // Handle case where shift_days are objects from API
        let daysAsNumbers: number[] = [];
        if (Array.isArray(currentShiftDays) && currentShiftDays.length > 0) {
             const firstItem = currentShiftDays[0];
             if (typeof firstItem === 'object' && firstItem !== null && 'day_of_week' in firstItem) {
                 daysAsNumbers = (currentShiftDays as any[]).map(d => d.day_of_week);
             } else {
                 daysAsNumbers = currentShiftDays as number[];
             }
        }
        
        setSelectedDays(daysAsNumbers);
      } else {
        reset({
          shift_description: "",
          shift_hours_start: "",
          shift_hours_end: "",
          break_time_start: "",
          break_time_end: "",
          timezone: "",
          shift_days: [],
        } as any);
        setSelectedDays([]);
      }
    }
  }, [open, initialData, reset]);

  const toggleDay = (day: number) => {
    const updated = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    setSelectedDays(updated);
    setValue("shift_days", updated, { shouldValidate: true, shouldDirty: true });
  };

  const onFormSubmit = (data: WorkShift) => {
    onSubmit({ ...data, shift_days: selectedDays });
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "edit" ? t("workShift:editTitle") : t("workShift:createTitle")}
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="shift_description" value={t("workShift:description")} />
            <TextInput
              id="shift_description"
              placeholder={t("workShift:descriptionPlaceholder")}
              {...register("shift_description", { required: t("workShift:descriptionRequired") })}
              color={errors.shift_description ? "failure" : undefined}
              helperText={errors.shift_description?.message}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="shift_hours_start" value={t("workShift:startTime")} />
              <TextInput
                id="shift_hours_start"
                type="time"
                {...register("shift_hours_start", { required: t("workShift:startTimeRequired") })}
                color={errors.shift_hours_start ? "failure" : undefined}
                helperText={errors.shift_hours_start?.message}
              />
            </div>
            <div>
              <Label htmlFor="shift_hours_end" value={t("workShift:endTime")} />
              <TextInput
                id="shift_hours_end"
                type="time"
                {...register("shift_hours_end", { required: t("workShift:endTimeRequired") })}
                color={errors.shift_hours_end ? "failure" : undefined}
                helperText={errors.shift_hours_end?.message}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="break_time_start" value={t("workShift:breakStartTime")} />
              <TextInput
                id="break_time_start"
                type="time"
                {...register("break_time_start")}
                color={errors.break_time_start ? "failure" : undefined}
                helperText={errors.break_time_start?.message}
              />
            </div>
            <div>
              <Label htmlFor="break_time_end" value={t("workShift:breakEndTime")} />
              <TextInput
                id="break_time_end"
                type="time"
                {...register("break_time_end")}
                color={errors.break_time_end ? "failure" : undefined}
                helperText={errors.break_time_end?.message}
              />
            </div>
          </div>

          <div>
              <Label htmlFor="timezone" value={t("workShift:timezone")} />
              <Select
                id="timezone"
                {...register("timezone", { required: t("workShift:timezoneRequired") })}
                color={errors.timezone ? "failure" : undefined}
                helperText={errors.timezone?.message}
              >
                  <option value="">{t("workShift:selectTimezone")}</option>
                  {timeZones.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
              </Select>
          </div>

          <div>
            <Label className="mb-2 block" value={t("workShift:shiftDays")} />
            <div className="flex gap-2 flex-wrap">
              {weekDays.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`w-10 h-10 rounded-md border text-sm font-semibold transition-colors ${
                    selectedDays.includes(day.value)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
            {selectedDays.length === 0 && (
                 <p className="text-xs text-red-600 mt-1">{t("workShift:daysRequired")}</p>
            )}
          </div>
          
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          {t("common:cancel")}
        </Button>
        <Button onClick={handleSubmit(onFormSubmit)}>
           {mode === "edit" ? t("common:saveChanges") : t("common:create")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
